// ═══════════════════════════════════════════════════════════════════
// Workshop3D.jsx — WILLIE'S WORKSHOP, IN THREE DIMENSIONS   (caP'n, 2026-09-22)
// ═══════════════════════════════════════════════════════════════════
// caP'n: "it's time to transform the Forge into a 3D forge … the 2D
// picture doesn't really look like the 3D outside tent thing, let's try
// to marry both ideas keeping vital points of the 2D and 3D … keep all
// the important stuff. I really wanna make the whole plaza 3D — the forge
// is the last thing missing."
//
// ⭐ THE MARRIAGE — IT IS BIGGER ON THE INSIDE.
//   OUTSIDE (plazaFixtures3D SculptedTent): a tattered, patched canvas
//   tent with a pole, ropes, a N'ullus forge and a mat of blue light at
//   the mouth.
//   INSIDE (p_forge.webp): a stone workshop — flagstones, a stone back
//   wall with the hearth, timber trusses, chandeliers, shelves of potions,
//   the cauldron, the grimoire, the chest, the circle.
//   HERE: you walk in under THE SAME patched canvas (plazaPieces3D's own
//   tentTex, seen from beneath), held up on the outside's wooden poles,
//   and it comes down onto the painting's stone. Waist-high stone, canvas
//   above it; a full stone back wall for the hearth; the mouth open to
//   the camera with the outside's mat of blue light on the threshold.
//
// ⭐ THIS IS A RENDERER, NOT A ROOM. (Plaza3D's law, again.)
//   WilliesWorkshop.js keeps every line of its movement, collision,
//   prompts, dialogue, the circle timer and the console. With the 3D
//   switch ON (plazaCamera.getPlaza3D — the SAME switch as the Plaza),
//   only its painted map <div> is not mounted and this stands in its
//   place. Placement is the workshop's own tile map: every fixture here
//   sits on tiles that map already makes SOLID, so nothing you can see is
//   something you can walk through that the 2D room would stop you at —
//   and nothing here collides, because collision is not this file's job.
//
// ⭐ WILLIE IS THE PLAZA'S WILLY, EXACTLY (caP'n: "copy paste the exact
//   same willie from the plaza and his desk"). `SculptedBench` fed the
//   Plaza's OWN herald rect, and FolkBody("willy") at the Plaza's OWN
//   offset from it (Plaza3D npcPlacements) — the pair is moved as one
//   group, never re-authored. No new model was made.
//
// ── THE CAMERA CONTRACT — the Plaza's, not a copy of it ─────────────
//   The view (Follow/Trail), the free pose, the DEFAULT counter and the
//   ground-forward are plazaCamera.js's, so a camera choice made in the
//   Plaza is the camera you have in the Forge, and WilliesWorkshop's ONE
//   movement loop rotates its key vector by `rotateGridInput` (identity
//   in 2D, proven by plazaCamera.selftest.mjs). The bar is Plaza3D's own
//   exported <CameraBar>.
//
// ── THE CUTAWAY — how a closed tent never blocks the lens ───────────
//   Every shell surface (canopy, canvas walls, the gable) is ONE-SIDED
//   and faces INTO the room. From inside you see all of it; from any
//   camera outside, the near surfaces cull away and you look straight in
//   — a dollhouse, with zero occlusion code. (DELETE THE DEGREE OF
//   FREEDOM, NOT THE BUG.) Nothing in the shell casts a shadow, or the
//   moonlight through the mouth would black out the whole room.
//
// ── PERF LAW ────────────────────────────────────────────────────────
//   React.memo host; everything live reaches the scene through REFS.
//   Three point lights (hearth, Willy's lantern — carried by his bench —
//   and the grimoire's crystal); everything else that glows is emissive.
//   Bottles, books and coins are instanced. No PRNG during render.
//
// ⚠ Duplicated from Plaza3D.jsx on purpose: NamePlate, KitBody/Companion,
//   the rig body. Plaza3D is being worked on in another session and does
//   not export them; importing from it was not worth editing it for.
//   If Plaza3D ever exports them, swap these for the imports.
// ═══════════════════════════════════════════════════════════════════
import React, { useRef, useMemo, useEffect, useState } from "react";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import { OrbitControls, Html } from "@react-three/drei";
import * as THREE from "three";
import { GraceAura3D } from "./GraceAura";
import { GRACES, renderableGrace } from "./kingsGrace";
import { getCompanionSprite } from "./engineIndex";
import { IEBeing, getBeingPalette, canvasTex, mulberry32 } from "./ieBeingModels";
import { measureSolid } from "./fieldBeings3D";
import { companionBodyOf } from "./companionBody3D";
import {
  getView, viewMult, isPosed, setPosed, resetToken, subscribe,
  setGroundForward, resetGroundForward,
} from "./plazaCamera";
import { LIGHT, G, C, STONE, DARK_STONE, IRON, woodTex, tentTex } from "./plazaPieces3D";
import { SculptedBench } from "./plazaFixtures3D";
import { FolkBody, folkHeight, folkIdFor } from "./plazaFolk3D";
import { CameraBar } from "./Plaza3D";
import { FrameWatch } from "./sculptGate";

// ═══════════════════════════════════════════════════════════════════
// ⭐ THE ROOM'S MEASUREMENTS — read off WilliesWorkshop.js's tile map.
//   world x = tile x, world z = tile y; a tile's centre is +0.5.
//   rows 1-4 wall            → the back wall's face is z 5
//   rows 4-9 at x 1-2 floor  → the ALCOVE, back face z 4 (The Dungeon)
//   rows 5-6 at x 3-12 wall  → the counter block, z 5..7 (Willie, hearth)
//   rows 4-10 at x 16-18     → the east block, x 16..19 (door, grimoire)
//   col 0 / col 19 / row 13  → the west, east and south faces
// ═══════════════════════════════════════════════════════════════════
const X0 = 1, X1 = 19, ZA = 4, ZB = 5, ZS = 13;
const CX = 10, HALF = 9;
const EAVE = 3.2;           // canvas meets the side walls here
const STONE_H = 1.7;        // the waist-high stone under the canvas
const LINTEL = 3.4;         // the back wall's stone ends; the timber gable begins
const RIDGE = 7.8, RIDGE_Z = 8.5;
const FRONT_Z = 13.6;       // the canopy's lifted front edge (the mouth)
const BATTEN = 18 / 8;      // canopy ribs every 2.25

// ── dials ─────────────────────────────────────────────────────────
const COMPANION_H = 0.95;
const LERP_K = 0.001;
const N_BOTTLES = 44;
const N_BOOKS = 30;
const N_COINS = 18;

// ── where each INTERACTABLE is SEEN (its ◆ floats here). The tile it
// answers on is still WilliesWorkshop's INTERACTABLES table; this is the
// look. An id with no row floats over its own tile.
const ANCHOR = {
  fireplace:    [11.3, 3.0, 5.4],
  grimoire:     [16.7, 2.25, 6.35],
  chest:        [3.6, 1.35, 6.45],
  willie:       [8.3, 2.35, 5.5],
  dungeon_door: [2.0, 1.35, 4.55],
};

// ── Willy and his bench: THE PLAZA'S OWN PLACEMENT, moved as one ─────
// plazaZones herald.solid = [[6,7,11,8]]; Plaza3D puts Willy at
// (b.cx + b.w*0.12, b.cz - 0.95) = (9.72, 7.05); SculptedBench's own
// place() puts the bench at (9.72, 8). We translate that pair so Willy
// stands behind the counter at tile (8, 5), over his "Speak" tile (8, 7).
const HERALD_RECT = [[6, 7, 11, 8]];
const PLAZA_WILLY = [9.72, 7.05];
const WILLY_AT = [8.3, 5.5];
const BENCH_SHIFT = [WILLY_AT[0] - PLAZA_WILLY[0], 0, WILLY_AT[1] - PLAZA_WILLY[1]];

// ═══════════════════════════════════════════════════════════════════
// THE CANOPY — the outside's canvas, from beneath.
// ═══════════════════════════════════════════════════════════════════
function canopyH(x, z) {
  const s = Math.min(1, Math.abs(x - CX) / HALF);
  const t = z - RIDGE_Z;
  const ridge = t < 0 ? RIDGE - 1.2 * (t / 4.5) ** 2 : RIDGE - 1.5 * (t / 5.1) ** 2;
  const bell = Math.pow(Math.max(0, 1 - s * s), 0.75);
  const sagX = Math.sin(Math.PI * (x - X0) / BATTEN) ** 2;
  const sag = 0.2 * sagX * bell * (0.6 + 0.4 * Math.sin(Math.PI * (z - ZB) / (FRONT_Z - ZB)));
  return EAVE + (ridge - EAVE) * bell - sag;
}
// a grid whose normals point UP (outward); drawn BackSide, you see it from below
function canopyGeometry(nx = 64, nz = 30) {
  const pos = [], uv = [], idx = [];
  for (let j = 0; j <= nz; j++) {
    const z = ZB + (FRONT_Z - ZB) * j / nz;
    for (let i = 0; i <= nx; i++) {
      const x = X0 + (X1 - X0) * i / nx;
      pos.push(x, canopyH(x, z), z);
      uv.push(x / 4.5, z / 4.5);
    }
  }
  const w = nx + 1;
  for (let j = 0; j < nz; j++) for (let i = 0; i < nx; i++) {
    const a = j * w + i, b = (j + 1) * w + i, c = a + 1, d = b + 1;
    idx.push(a, b, c, c, b, d);
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute("position", new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute("uv", new THREE.Float32BufferAttribute(uv, 2));
  g.setIndex(idx); g.computeVertexNormals();
  return g;
}
// the timber gable over the back wall: a strip from the lintel to the canopy, facing south
function gableGeometry(n = 48) {
  const pos = [], uv = [], idx = [];
  for (let i = 0; i <= n; i++) {
    const x = X0 + (X1 - X0) * i / n;
    const top = Math.max(LINTEL, canopyH(x, ZB) + 0.04);
    pos.push(x, LINTEL, ZB + 0.01, x, top, ZB + 0.01);
    uv.push(x / 3, LINTEL / 3, x / 3, top / 3);
  }
  for (let i = 0; i < n; i++) { const a = i * 2, c = a + 1, b = a + 2, d = a + 3; idx.push(a, b, c, b, d, c); }
  const g = new THREE.BufferGeometry();
  g.setAttribute("position", new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute("uv", new THREE.Float32BufferAttribute(uv, 2));
  g.setIndex(idx); g.computeVertexNormals();
  return g;
}
const canopyCurve = (x, z0 = ZB, z1 = FRONT_Z, n = 14, drop = 0.03) => new THREE.CatmullRomCurve3(
  Array.from({ length: n + 1 }, (_, k) => { const z = z0 + (z1 - z0) * k / n; return new THREE.Vector3(x, canopyH(x, z) - drop, z); }));
const frontCurve = (n = 24) => new THREE.CatmullRomCurve3(
  Array.from({ length: n + 1 }, (_, k) => { const x = X0 + (X1 - X0) * k / n; return new THREE.Vector3(x, canopyH(x, FRONT_Z) - 0.03, FRONT_Z); }));

// ═══════════════════════════════════════════════════════════════════
// TEXTURES — painted once, cached by key, seeded. Off p_forge.webp.
// ═══════════════════════════════════════════════════════════════════
const rep = (t, x, y) => { const r = t.clone(); r.wrapS = r.wrapT = THREE.RepeatWrapping; r.repeat.set(x, y); r.needsUpdate = true; return r; };

// the painting's floor: big slate flags, cool grey-green, cracked, mossy seams
const flagTex = () => canvasTex("ww3d:flag", 512, 512, (g, w, h) => {
  const R = mulberry32(0xf1a6);
  g.fillStyle = "#15141a"; g.fillRect(0, 0, w, h);
  const n = 4, s = w / n;
  for (let j = 0; j < n; j++) for (let i = 0; i < n; i++) {
    const l = 24 + R() * 10, hue = 200 + R() * 60;
    g.fillStyle = `hsl(${hue}, ${8 + R() * 8}%, ${l}%)`;
    g.fillRect(i * s + 3, j * s + 3, s - 6, s - 6);
    for (let k = 0; k < 60; k++) { g.fillStyle = `rgba(${R() < 0.5 ? "0,0,0" : "200,210,220"},${R() * 0.06})`; g.fillRect(i * s + R() * s, j * s + R() * s, 2 + R() * 10, 2 + R() * 6); }
    g.fillStyle = "rgba(220,230,240,0.06)"; g.fillRect(i * s + 3, j * s + 3, s - 6, 2);
  }
  // moss in the seams
  g.strokeStyle = "rgba(70,100,50,0.35)"; g.lineWidth = 3;
  for (let k = 0; k <= n; k++) { if (R() < 0.6) { g.beginPath(); g.moveTo(k * s, 0); g.lineTo(k * s, h); g.stroke(); } if (R() < 0.6) { g.beginPath(); g.moveTo(0, k * s); g.lineTo(w, k * s); g.stroke(); } }
  // cracks
  g.strokeStyle = "rgba(5,5,8,0.8)"; g.lineWidth = 1.6;
  for (let i = 0; i < 9; i++) { let x = R() * w, y = R() * h; g.beginPath(); g.moveTo(x, y); for (let k = 0; k < 6; k++) { x += (R() - 0.5) * 50; y += (R() - 0.5) * 50; g.lineTo(x, y); } g.stroke(); }
});
// the painting's walls: big ashlar blocks, cold grey with a green cast
const blockTex = (key = "ww3d:block", light = 30) => canvasTex(key, 512, 512, (g, w, h) => {
  const R = mulberry32(0xb10c + light);
  g.fillStyle = "#0e0e12"; g.fillRect(0, 0, w, h);
  const bh = 42, bw = 86;
  for (let y = 0; y < h; y += bh) {
    const off = ((y / bh) | 0) % 2 ? bw / 2 : 0;
    for (let x = -bw; x < w + bw; x += bw) {
      g.fillStyle = `hsl(${190 + R() * 50}, ${6 + R() * 8}%, ${light - 6 + R() * 10}%)`;
      g.fillRect(x + off + 2, y + 2, bw - 4, bh - 4);
      g.fillStyle = `rgba(220,230,240,${0.04 + R() * 0.05})`; g.fillRect(x + off + 2, y + 2, bw - 4, 2);
      g.fillStyle = `rgba(0,0,0,${0.15 + R() * 0.1})`; g.fillRect(x + off + 2, y + bh - 5, bw - 4, 3);
    }
  }
  for (let i = 0; i < 26; i++) { const x = R() * w, y = R() * h, r = 20 + R() * 60; const gr = g.createRadialGradient(x, y, 0, x, y, r); gr.addColorStop(0, `rgba(40,60,30,${0.12 + R() * 0.12})`); gr.addColorStop(1, "rgba(0,0,0,0)"); g.fillStyle = gr; g.fillRect(x - r, y - r, r * 2, r * 2); }
});
// the circle: a hexagram in two rings, teal bleeding into violet (as painted)
const circleTex = () => canvasTex("ww3d:circle", 512, 512, (g, w, h) => {
  g.clearRect(0, 0, w, h);
  const cx = w / 2, cy = h / 2;
  const grad = g.createLinearGradient(0, 0, w, 0); grad.addColorStop(0, "#3fe0c8"); grad.addColorStop(1, "#b36bff");
  g.strokeStyle = grad; g.fillStyle = grad; g.shadowColor = "#7fe8d8"; g.shadowBlur = 12; g.lineCap = "round";
  [236, 206, 196].forEach((r, i) => { g.lineWidth = i === 0 ? 7 : 3; g.beginPath(); g.arc(cx, cy, r, 0, Math.PI * 2); g.stroke(); });
  const star = (rot) => { g.beginPath(); for (let k = 0; k < 3; k++) { const a = rot + k * Math.PI * 2 / 3; const x = cx + Math.cos(a) * 196, y = cy + Math.sin(a) * 196; if (k) g.lineTo(x, y); else g.moveTo(x, y); } g.closePath(); g.lineWidth = 5; g.stroke(); };
  star(-Math.PI / 2); star(Math.PI / 2);
  g.lineWidth = 3; g.beginPath(); g.arc(cx, cy, 80, 0, Math.PI * 2); g.stroke();
  g.font = "bold 22px Georgia, serif"; g.textAlign = "center"; g.textBaseline = "middle";
  const GL = "ᚠᚢᚦᚨᚱᚲᚷᚹᚺᚾᛁᛃᛇᛈᛉᛊᛏᛒᛖᛗᛚᛜᛞᛟ";
  for (let k = 0; k < 24; k++) { const a = k / 24 * Math.PI * 2; g.save(); g.translate(cx + Math.cos(a) * 221, cy + Math.sin(a) * 221); g.rotate(a + Math.PI / 2); g.fillText(GL[k], 0, 0); g.restore(); }
});
// The Dungeon: a doorway that is only dark, with violet at the edges
const voidTex = () => canvasTex("ww3d:void", 256, 256, (g, w, h) => {
  const gr = g.createRadialGradient(w / 2, h * 0.62, 10, w / 2, h * 0.62, w * 0.62);
  gr.addColorStop(0, "#000000"); gr.addColorStop(0.55, "#030108"); gr.addColorStop(0.85, "#2a0d52"); gr.addColorStop(1, "#6d28d9");
  g.fillStyle = gr; g.fillRect(0, 0, w, h);
});
const pageTex = () => canvasTex("ww3d:page", 256, 160, (g, w, h) => {
  const R = mulberry32(0x9a6e);
  g.fillStyle = "#e7d6ff"; g.fillRect(0, 0, w, h);
  g.fillStyle = "#6d28d9";
  for (let side = 0; side < 2; side++) for (let y = 16; y < h - 10; y += 11) { let x = side * w / 2 + 12; while (x < side * w / 2 + w / 2 - 14) { const l = 4 + R() * 14; g.fillRect(x, y, l, 3); x += l + 4; } }
  g.fillStyle = "rgba(90,40,160,0.5)"; g.fillRect(w / 2 - 2, 0, 4, h);
  g.strokeStyle = "#b36bff"; g.lineWidth = 2; g.beginPath(); g.arc(w * 0.75, h * 0.5, 24, 0, Math.PI * 2); g.stroke();
});
const plaqueTex = () => canvasTex("ww3d:plaque", 256, 64, (g, w, h) => {
  g.fillStyle = "#1a1420"; g.fillRect(0, 0, w, h);
  g.strokeStyle = "#6d28d9"; g.lineWidth = 3; g.strokeRect(4, 4, w - 8, h - 8);
  g.fillStyle = "#c9b3ff"; g.font = "bold 26px Georgia, serif"; g.textAlign = "center"; g.textBaseline = "middle";
  g.shadowColor = "#6d28d9"; g.shadowBlur = 10; g.fillText("THE DUNGEON", w / 2, h / 2 + 2);
});

// ═══════════════════════════════════════════════════════════════════
// THE SHELL — floor, stone, canvas, the gable, the canopy, the poles.
// ═══════════════════════════════════════════════════════════════════
function Shell() {
  const floor = useMemo(() => rep(flagTex(), (X1 - X0) / 2, (FRONT_Z + 0.4 - ZA) / 2), []);
  const stone = useMemo(() => rep(blockTex(), 4, 0.8), []);
  const stoneTall = useMemo(() => rep(blockTex(), 5, 1.4), []);
  const gableStone = useMemo(() => { const t = rep(blockTex("ww3d:block-dark", 20), 1, 1); return t; }, []);
  const canvasTop = useMemo(() => rep(tentTex(), 1, 1), []);
  const canvasWall = useMemo(() => rep(tentTex(), 3, 0.5), []);
  const wood = useMemo(() => woodTex(), []);
  const canopy = useMemo(() => canopyGeometry(), []);
  const gable = useMemo(() => gableGeometry(), []);
  const ribs = useMemo(() => Array.from({ length: 9 }, (_, k) => new THREE.TubeGeometry(canopyCurve(X0 + k * BATTEN), 28, 0.05, 5)), []);
  const hem = useMemo(() => new THREE.TubeGeometry(frontCurve(), 48, 0.07, 6), []);
  // the valance — a scalloped canvas hem hanging off the lifted front edge
  const valance = useMemo(() => {
    const out = []; const n = 24;
    for (let k = 0; k < n; k++) { const x = X0 + (X1 - X0) * (k + 0.5) / n; out.push({ x, y: canopyH(x, FRONT_Z) - 0.25 }); }
    return out;
  }, []);
  const lowerWest = (FRONT_Z - ZA);
  return (
    <group>
      {/* the ground past the tent, so the mouth never opens onto nothing */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[CX, -0.02, 10]}><planeGeometry args={[80, 60]} /><meshStandardMaterial color="#0b0812" roughness={1} /></mesh>
      {/* the floor */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[CX, 0, (ZA + FRONT_Z + 0.4) / 2]} receiveShadow>
        <planeGeometry args={[X1 - X0, FRONT_Z + 0.4 - ZA]} />
        <meshStandardMaterial map={floor} roughness={0.92} />
      </mesh>
      {/* the outside's mat of blue light, at the threshold (the mouth is the same mouth) */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[9.5, 0.015, 12.7]}><circleGeometry args={[1.3, 20]} /><meshBasicMaterial color="#7ac8ff" transparent opacity={0.07} depthWrite={false} toneMapped={false} /></mesh>

      {/* ── THE BACK WALL — full stone to the lintel, as painted ── */}
      <mesh position={[(3 + X1) / 2, LINTEL / 2, ZB]} receiveShadow><planeGeometry args={[X1 - 3, LINTEL]} /><meshStandardMaterial map={stoneTall} roughness={0.9} /></mesh>
      {/* the alcove (x 1-3, back to z 4) — The Dungeon's recess */}
      <mesh position={[2, LINTEL / 2, ZA]} receiveShadow><planeGeometry args={[2, LINTEL]} /><meshStandardMaterial map={stone} color="#8a8a96" roughness={0.9} /></mesh>
      <mesh position={[3, LINTEL / 2, (ZA + ZB) / 2]} rotation={[0, -Math.PI / 2, 0]}><planeGeometry args={[ZB - ZA, LINTEL]} /><meshStandardMaterial map={stone} color="#8a8a96" roughness={0.9} /></mesh>
      {/* the gable: dark stone between timbers, up to the canvas */}
      <mesh geometry={gable}><meshStandardMaterial map={gableStone} color="#9a92a8" roughness={0.95} /></mesh>
      <Trusses wood={wood} />

      {/* ── THE SIDE WALLS — waist-high stone, the outside's canvas above ── */}
      <mesh position={[X0, STONE_H / 2, (ZA + FRONT_Z) / 2]} rotation={[0, Math.PI / 2, 0]} receiveShadow><planeGeometry args={[lowerWest, STONE_H]} /><meshStandardMaterial map={stone} roughness={0.9} /></mesh>
      <mesh position={[X0, (STONE_H + EAVE) / 2, (ZA + FRONT_Z) / 2]} rotation={[0, Math.PI / 2, 0]}><planeGeometry args={[lowerWest, EAVE - STONE_H]} /><meshStandardMaterial map={canvasWall} color="#8a7a62" emissive="#1c140c" emissiveMap={canvasWall} roughness={1} /></mesh>
      <mesh position={[X1, STONE_H / 2, (ZA + FRONT_Z) / 2]} rotation={[0, -Math.PI / 2, 0]} receiveShadow><planeGeometry args={[lowerWest, STONE_H]} /><meshStandardMaterial map={stone} roughness={0.9} /></mesh>
      <mesh position={[X1, (STONE_H + EAVE) / 2, (ZA + FRONT_Z) / 2]} rotation={[0, -Math.PI / 2, 0]}><planeGeometry args={[lowerWest, EAVE - STONE_H]} /><meshStandardMaterial map={canvasWall} color="#8a7a62" emissive="#1c140c" emissiveMap={canvasWall} roughness={1} /></mesh>
      {/* the stone cap where stone meets canvas */}
      {[X0 + 0.08, X1 - 0.08].map((x) => <mesh key={x} position={[x, STONE_H, (ZA + FRONT_Z) / 2]}><boxGeometry args={[0.16, 0.1, FRONT_Z - ZA]} /><meshStandardMaterial {...STONE} /></mesh>)}

      {/* ── THE CANOPY — the outside's patched canvas, seen from beneath ── */}
      <mesh geometry={canopy}><meshStandardMaterial map={canvasTop} color="#8e7c62" emissive="#221810" emissiveMap={canvasTop} side={THREE.BackSide} roughness={1} /></mesh>
      {ribs.map((g, k) => <mesh key={k} geometry={g}><meshStandardMaterial map={wood} roughness={0.9} /></mesh>)}
      <mesh geometry={hem}><meshStandardMaterial map={wood} roughness={0.9} /></mesh>
      {valance.map((v, k) => (
        <mesh key={k} position={[v.x, v.y, FRONT_Z + 0.01]} rotation={[0, 0, 0]}>
          <circleGeometry args={[0.4, 10, Math.PI, Math.PI]} />
          <meshStandardMaterial map={canvasTop} color="#8e7c62" emissive="#221810" emissiveMap={canvasTop} side={THREE.DoubleSide} roughness={1} />
        </mesh>
      ))}
      {/* the outside's poles: two at the mouth's corners, lashed to the hem */}
      {[X0 + 0.25, X1 - 0.25].map((x) => (
        <group key={x}>
          <mesh position={[x, canopyH(x, FRONT_Z) / 2, FRONT_Z - 0.1]} castShadow><cylinderGeometry args={[0.07, 0.1, canopyH(x, FRONT_Z), 6]} /><meshStandardMaterial map={wood} /></mesh>
          <mesh position={[x, canopyH(x, FRONT_Z) - 0.05, FRONT_Z - 0.1]}><sphereGeometry args={[0.1, 8, 6]} /><meshStandardMaterial map={wood} /></mesh>
        </group>
      ))}
      {/* the south lip — a low stone curb, and the painting's foreground bottles on it */}
      <mesh position={[CX, 0.19, ZS + 0.18]} castShadow receiveShadow><boxGeometry args={[X1 - X0, 0.38, 0.36]} /><meshStandardMaterial {...DARK_STONE} /></mesh>
      {[[2.6, "#3fa66a"], [3.1, "#c0304a"], [6.7, "#c0304a"], [7.2, "#3f7fb0"], [14.1, "#3fa66a"], [14.6, "#c0304a"], [17.4, "#8a5cc8"]].map(([x, c], i) => (
        <group key={i} position={[x, 0.38, ZS + 0.18]}>
          <mesh position={[0, 0.13, 0]}><sphereGeometry args={[0.12, 10, 8]} /><meshStandardMaterial color={c} emissive={c} emissiveIntensity={0.35} transparent opacity={0.85} roughness={0.15} /></mesh>
          <mesh position={[0, 0.3, 0]}><cylinderGeometry args={[0.035, 0.045, 0.14, 6]} /><meshStandardMaterial color={c} transparent opacity={0.85} /></mesh>
          <mesh position={[0, 0.39, 0]}><cylinderGeometry args={[0.04, 0.04, 0.05, 6]} /><meshStandardMaterial color="#6b4a2a" /></mesh>
        </group>
      ))}
    </group>
  );
}

// the painting's timber: a lintel, a plate, posts and X braces across the gable
function Trusses({ wood }) {
  const posts = [3, 6.2, 9.4, 13.2, 16.4, 19];
  const plateY = 5.1;
  const beam = (a, b, t = 0.18, i) => {
    const dx = b[0] - a[0], dy = b[1] - a[1], len = Math.hypot(dx, dy);
    return <mesh key={i} position={[(a[0] + b[0]) / 2, (a[1] + b[1]) / 2, ZB + 0.08]} rotation={[0, 0, Math.atan2(dy, dx)]}><boxGeometry args={[len, t, 0.14]} /><meshStandardMaterial map={wood} roughness={0.85} /></mesh>;
  };
  const els = [];
  els.push(beam([X0, LINTEL], [X1, LINTEL], 0.26, "lintel"));
  els.push(beam([3, plateY], [17, plateY], 0.2, "plate"));
  posts.forEach((x, i) => { const top = Math.min(canopyH(x, ZB), 7.6); if (top > LINTEL + 0.2) els.push(beam([x, LINTEL], [x, top], 0.2, "p" + i)); });
  [[6.2, 9.4], [13.2, 16.4]].forEach(([a, b], i) => { els.push(beam([a, LINTEL + 0.1], [b, plateY - 0.1], 0.14, "xa" + i)); els.push(beam([a, plateY - 0.1], [b, LINTEL + 0.1], 0.14, "xb" + i)); });
  return <group>{els}</group>;
}

// ═══════════════════════════════════════════════════════════════════
// THE HEARTH — The Forge. The painting's stone fireplace with its hood;
// the fire is the painting's orange over the outside forge's violet
// ember-bed. ONE point light.
// ═══════════════════════════════════════════════════════════════════
const HEARTH_X = 11.3;
function Hearth() {
  const stone = useMemo(() => rep(blockTex(), 1, 1), []);
  const wood = useMemo(() => woodTex(), []);
  const fire = useRef(); const fire2 = useRef(); const ember = useRef(); const light = useRef();
  useFrame(({ clock }) => {
    const t = clock.getElapsedTime();
    const k = 1 + Math.sin(t * 5.3) * 0.08 + Math.sin(t * 9.1) * 0.05;
    if (fire.current) { fire.current.scale.set(1, k, 1); fire.current.material.opacity = 0.82 + Math.sin(t * 7) * 0.08; }
    if (fire2.current) { fire2.current.scale.set(1, 2 - k, 1); }
    if (ember.current) ember.current.material.emissiveIntensity = 0.8 + Math.sin(t * 2.2) * 0.25;
    if (light.current) light.current.intensity = G(LIGHT.forge) * 1.25 * k;
  });
  const hoodTop = canopyH(HEARTH_X, ZB) + 0.3;
  return (
    <group position={[HEARTH_X, 0, ZB]}>
      {/* the surround: two jambs, a lintel arch, the mantel */}
      {[-1, 1].map((s) => <mesh key={s} position={[s * 1.05, 0.95, 0.3]} castShadow><boxGeometry args={[0.5, 1.9, 0.6]} /><meshStandardMaterial map={stone} color="#b0b0bc" roughness={0.9} /></mesh>)}
      <mesh position={[0, 1.95, 0.3]} castShadow><boxGeometry args={[2.6, 0.3, 0.64]} /><meshStandardMaterial map={stone} color="#b0b0bc" roughness={0.9} /></mesh>
      <mesh position={[0, 1.55, 0.58]} rotation={[0, 0, 0]}><torusGeometry args={[0.8, 0.09, 6, 16, Math.PI]} /><meshStandardMaterial {...STONE} /></mesh>
      <mesh position={[0, 2.15, 0.36]}><boxGeometry args={[2.9, 0.12, 0.78]} /><meshStandardMaterial map={wood} /></mesh>
      {/* the firebox — dark inside, a violet ember-bed, the orange fire */}
      <mesh position={[0, 0.9, 0.06]}><boxGeometry args={[1.6, 1.8, 0.1]} /><meshStandardMaterial color="#0a0608" roughness={1} /></mesh>
      <mesh position={[0, 0.12, 0.35]}><boxGeometry args={[1.7, 0.24, 0.62]} /><meshStandardMaterial {...DARK_STONE} /></mesh>
      <mesh ref={ember} position={[0, 0.25, 0.32]} rotation={[-Math.PI / 2, 0, 0]}><circleGeometry args={[0.55, 10]} /><meshStandardMaterial color="#6a3ae0" emissive="#7c4cff" emissiveIntensity={0.9} toneMapped={false} /></mesh>
      {[-0.3, 0, 0.28].map((x, i) => <mesh key={i} position={[x, 0.33, 0.32]} rotation={[0, i, Math.PI / 2]}><cylinderGeometry args={[0.07, 0.08, 0.7, 6]} /><meshStandardMaterial color="#2a1a10" emissive="#ff5a1a" emissiveIntensity={0.25} /></mesh>)}
      <mesh ref={fire} position={[0, 0.72, 0.3]}><coneGeometry args={[0.42, 0.95, 8]} /><meshBasicMaterial color="#ff9a2a" transparent opacity={0.85} depthWrite={false} toneMapped={false} /></mesh>
      <mesh ref={fire2} position={[0, 0.66, 0.34]}><coneGeometry args={[0.24, 0.62, 7]} /><meshBasicMaterial color="#ffe08a" transparent opacity={0.9} depthWrite={false} toneMapped={false} /></mesh>
      <pointLight ref={light} position={[0, 1.0, 0.9]} intensity={G(LIGHT.forge) * 1.25} distance={11} decay={1.3} color="#ffa04a" castShadow={false} />
      {/* the hood, up through the canvas — the tent's chimney */}
      <mesh position={[0, (2.2 + hoodTop) / 2, 0.28]}><cylinderGeometry args={[0.62, 1.2, hoodTop - 2.2, 4, 1, true, Math.PI / 4]} /><meshStandardMaterial map={stone} color="#8a8a96" roughness={0.9} side={THREE.DoubleSide} /></mesh>
      {/* the painting's two horned skulls on the pillars either side */}
      {[-1.9, 1.9].map((x) => <Skull key={x} position={[x, 2.55, 0.12]} scale={0.8} />)}
      {/* the hearth apron, tongs and a coal scuttle */}
      <mesh position={[0, 0.06, 1.0]} receiveShadow><boxGeometry args={[3.0, 0.12, 0.9]} /><meshStandardMaterial {...STONE} /></mesh>
      <group position={[0.95, 0, 0.95]} rotation={[0, 0, 0.2]}>
        {[-0.04, 0.04].map((x) => <mesh key={x} position={[x, 0.55, 0]}><cylinderGeometry args={[0.012, 0.012, 1.1, 4]} /><meshStandardMaterial {...IRON} /></mesh>)}
      </group>
      <mesh position={[-1.3, 0.2, 1.05]}><cylinderGeometry args={[0.2, 0.16, 0.34, 10]} /><meshStandardMaterial {...IRON} /></mesh>
      {/* the outside's anvil on its stump — the tent is still a forge */}
      <group position={[1.4, 0, 1.55]} rotation={[0, -0.35, 0]}>
        <mesh position={[0, 0.25, 0]} castShadow><cylinderGeometry args={[0.22, 0.26, 0.5, 7]} /><meshStandardMaterial map={wood} /></mesh>
        <mesh position={[0, 0.6, 0]} castShadow><boxGeometry args={[0.7, 0.2, 0.3]} /><meshStandardMaterial {...IRON} /></mesh>
        <mesh position={[0.42, 0.62, 0]} rotation={[0, 0, Math.PI / 2]}><coneGeometry args={[0.1, 0.3, 6]} /><meshStandardMaterial {...IRON} /></mesh>
        <mesh position={[-0.1, 0.72, 0.02]} rotation={[0, 0.4, 0]}><boxGeometry args={[0.24, 0.07, 0.1]} /><meshStandardMaterial {...IRON} /></mesh>
      </group>
    </group>
  );
}
// a horned animal skull (the painting's wall trophies); eyes lit when `eyes`
function Skull({ eyes = null, ...p }) {
  const BONE = { color: "#d8d0bc", roughness: 0.7 };
  return (
    <group {...p}>
      <mesh scale={[1, 0.85, 0.9]}><sphereGeometry args={[0.2, 12, 10]} /><meshStandardMaterial {...BONE} /></mesh>
      <mesh position={[0, -0.14, 0.1]} scale={[0.7, 0.6, 1]}><sphereGeometry args={[0.14, 10, 8]} /><meshStandardMaterial {...BONE} /></mesh>
      {[-1, 1].map((s) => (
        <group key={s}>
          <mesh position={[s * 0.08, 0.0, 0.16]}><sphereGeometry args={[0.05, 8, 6]} />{eyes ? <meshBasicMaterial color={eyes} toneMapped={false} /> : <meshStandardMaterial color="#120c10" />}</mesh>
          <mesh position={[s * 0.24, 0.12, 0]} rotation={[0, 0, -s * 0.9]}><coneGeometry args={[0.05, 0.34, 6]} /><meshStandardMaterial {...BONE} /></mesh>
        </group>
      ))}
    </group>
  );
}

// ═══════════════════════════════════════════════════════════════════
// THE DUNGEON — Willee's Library. caP'n: "it doesn't have to be a door …
// an interactable skull that 'opens' a wall (it's just a black empty void
// doorway)". A stone arch in the alcove's back wall, a doorway of nothing,
// three steps going down into it, a skull on the keystone watching you.
// The W'ilys hate to read, so they named the library "The Dungeon". Canon.
// ═══════════════════════════════════════════════════════════════════
function DungeonDoor() {
  const vt = useMemo(() => voidTex(), []);
  const plaque = useMemo(() => plaqueTex(), []);
  const glow = useRef();
  useFrame(({ clock }) => { const t = clock.getElapsedTime(); if (glow.current) glow.current.material.opacity = 0.16 + Math.sin(t * 1.6) * 0.06; });
  const W = 1.3, H = 2.0;
  return (
    <group position={[2, 0, ZA]}>
      <mesh position={[0, H / 2, 0.012]}><planeGeometry args={[W, H]} /><meshBasicMaterial map={vt} toneMapped={false} /></mesh>
      <mesh position={[0, H, 0.012]}><circleGeometry args={[W / 2, 20, 0, Math.PI]} /><meshBasicMaterial color="#000000" /></mesh>
      {/* the steps, going down into it */}
      {[0, 1, 2].map((k) => <mesh key={k} position={[0, 0.06 - k * 0.001, 0.12 + k * 0.001]} scale={[1 - k * 0.12, 1, 1]}><boxGeometry args={[W * 0.95, 0.02, 0.24 - k * 0.06]} /><meshStandardMaterial color={k === 0 ? "#3a3440" : "#1a1620"} /></mesh>)}
      {/* the arch */}
      {[-1, 1].map((s) => <mesh key={s} position={[s * (W / 2 + 0.14), H / 2, 0.1]} castShadow><boxGeometry args={[0.28, H, 0.26]} /><meshStandardMaterial {...STONE} /></mesh>)}
      <mesh position={[0, H, 0.1]}><torusGeometry args={[W / 2 + 0.14, 0.15, 6, 18, Math.PI]} /><meshStandardMaterial {...STONE} /></mesh>
      <Skull position={[0, H + W / 2 + 0.18, 0.2]} scale={0.9} eyes="#b388ff" />
      {/* carved into the timber lintel over the recess, where the room can read it */}
      <mesh position={[0, LINTEL, ZB - ZA + 0.17]}><planeGeometry args={[1.1, 0.26]} /><meshBasicMaterial map={plaque} toneMapped={false} /></mesh>
      {/* the violet that leaks out onto the flags */}
      <mesh ref={glow} rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.02, 0.9]}><circleGeometry args={[1.0, 18]} /><meshBasicMaterial color="#6d28d9" transparent opacity={0.18} depthWrite={false} toneMapped={false} /></mesh>
    </group>
  );
}

// ═══════════════════════════════════════════════════════════════════
// THE WEST WALL — the painting's potion shelves (instanced bottles), the
// chest (on the counter's corner, so nothing you walk through), the
// cauldron with its teal fire, the worktop of jars and crates.
// ═══════════════════════════════════════════════════════════════════
function PotionShelves() {
  const wood = useMemo(() => woodTex(), []);
  const ref = useRef();
  const Z0 = 8.6, Z1 = 12.7, D = 0.42, SH = [0.35, 0.95, 1.55, 2.15];
  const bottles = useMemo(() => {
    const R = mulberry32(0xb077);
    const cols = ["#3fa66a", "#c0304a", "#3f7fb0", "#8a5cc8", "#d6a23a", "#5fd0c0", "#9a3a2a"];
    const out = [];
    for (let i = 0; i < N_BOTTLES; i++) {
      const sh = SH[i % SH.length];
      out.push({ z: Z0 + 0.15 + R() * (Z1 - Z0 - 0.3), y: sh + 0.03, s: 0.7 + R() * 0.7, c: cols[(R() * cols.length) | 0] });
    }
    return out;
  }, []);
  useEffect(() => {
    const m = ref.current; if (!m) return;
    const o = new THREE.Object3D(); const col = new THREE.Color();
    bottles.forEach((b, i) => { o.position.set(X0 + 0.22, b.y + 0.14 * b.s, b.z); o.scale.set(b.s, b.s, b.s); o.updateMatrix(); m.setMatrixAt(i, o.matrix); m.setColorAt(i, col.set(b.c)); });
    m.instanceMatrix.needsUpdate = true; if (m.instanceColor) m.instanceColor.needsUpdate = true;
  }, [bottles]);
  return (
    <group>
      {/* uprights and boards */}
      {[Z0, (Z0 + Z1) / 2, Z1].map((z) => <mesh key={z} position={[X0 + D / 2, 1.3, z]} castShadow><boxGeometry args={[D, 2.6, 0.08]} /><meshStandardMaterial map={wood} /></mesh>)}
      {SH.map((y) => <mesh key={y} position={[X0 + D / 2, y, (Z0 + Z1) / 2]} castShadow receiveShadow><boxGeometry args={[D, 0.06, Z1 - Z0]} /><meshStandardMaterial map={wood} /></mesh>)}
      <mesh position={[X0 + D / 2, 2.62, (Z0 + Z1) / 2]}><boxGeometry args={[D + 0.06, 0.08, Z1 - Z0 + 0.1]} /><meshStandardMaterial map={wood} /></mesh>
      <instancedMesh ref={ref} args={[null, null, N_BOTTLES]}>
        <cylinderGeometry args={[0.07, 0.09, 0.28, 8]} />
        <meshStandardMaterial emissive="#222" roughness={0.15} transparent opacity={0.88} />
      </instancedMesh>
    </group>
  );
}
function Chest() {
  const wood = useMemo(() => woodTex("ww3d:chestwood", 26, 0x3c), []);
  return (
    <group position={[3.6, 0, 6.45]} rotation={[0, 0.25, 0]}>
      <mesh position={[0, 0.28, 0]} castShadow><boxGeometry args={[0.95, 0.56, 0.62]} /><meshStandardMaterial map={wood} /></mesh>
      <mesh position={[0, 0.56, 0]} rotation={[0, 0, Math.PI / 2]} scale={[1, 1, 1]}><cylinderGeometry args={[0.31, 0.31, 0.95, 12, 1, false, 0, Math.PI]} /><meshStandardMaterial map={wood} /></mesh>
      {[-0.35, 0, 0.35].map((x) => <mesh key={x} position={[x, 0.45, 0]}><boxGeometry args={[0.06, 0.95, 0.66]} /><meshStandardMaterial {...IRON} color="#6a6470" /></mesh>)}
      <mesh position={[0, 0.5, 0.33]}><boxGeometry args={[0.14, 0.16, 0.04]} /><meshStandardMaterial color="#c9a84c" metalness={0.8} roughness={0.3} /></mesh>
    </group>
  );
}
function Cauldron() {
  const fire = useRef(); const brew = useRef();
  useFrame(({ clock }) => {
    const t = clock.getElapsedTime();
    if (fire.current) fire.current.scale.set(1, 1 + Math.sin(t * 6) * 0.12, 1);
    if (brew.current) brew.current.material.emissiveIntensity = 0.9 + Math.sin(t * 2.4) * 0.3;
  });
  return (
    <group position={[5.5, 0, 6.2]}>
      {[0, 2.1, 4.2].map((a) => <mesh key={a} position={[Math.sin(a) * 0.36, 0.3, Math.cos(a) * 0.36]} rotation={[Math.cos(a) * 0.2, 0, -Math.sin(a) * 0.2]}><cylinderGeometry args={[0.03, 0.03, 0.62, 5]} /><meshStandardMaterial {...IRON} /></mesh>)}
      <mesh position={[0, 0.82, 0]} castShadow><sphereGeometry args={[0.46, 16, 12, 0, Math.PI * 2, Math.PI * 0.28, Math.PI * 0.72]} /><meshStandardMaterial color="#1e1c24" metalness={0.6} roughness={0.5} side={THREE.DoubleSide} /></mesh>
      <mesh position={[0, 1.2, 0]} rotation={[Math.PI / 2, 0, 0]}><torusGeometry args={[0.36, 0.05, 6, 18]} /><meshStandardMaterial {...IRON} /></mesh>
      <mesh ref={brew} position={[0, 1.16, 0]} rotation={[-Math.PI / 2, 0, 0]}><circleGeometry args={[0.34, 16]} /><meshStandardMaterial color="#1fbfa0" emissive="#3fe0c8" emissiveIntensity={1} toneMapped={false} /></mesh>
      <mesh ref={fire} position={[0, 1.5, 0]}><coneGeometry args={[0.26, 0.6, 8, 1, true]} /><meshBasicMaterial color="#5fffd8" transparent opacity={0.45} depthWrite={false} toneMapped={false} side={THREE.DoubleSide} /></mesh>
      <mesh position={[0, 0.15, 0]}><coneGeometry args={[0.2, 0.3, 6]} /><meshBasicMaterial color="#3fe0c8" transparent opacity={0.7} toneMapped={false} /></mesh>
    </group>
  );
}
function Worktop() {
  const wood = useMemo(() => woodTex(), []);
  return (
    <group>
      {/* the long counter along the back, as painted, left of Willie */}
      <mesh position={[4.9, 0.85, ZB + 0.45]} castShadow receiveShadow><boxGeometry args={[3.6, 0.1, 0.8]} /><meshStandardMaterial map={wood} /></mesh>
      {[3.25, 6.55].map((x) => <mesh key={x} position={[x, 0.42, ZB + 0.45]}><boxGeometry args={[0.12, 0.84, 0.7]} /><meshStandardMaterial map={wood} /></mesh>)}
      {/* a barrel, a crate, jars, a candle, rolled scrolls */}
      <mesh position={[3.6, 1.25, ZB + 0.4]} castShadow><cylinderGeometry args={[0.28, 0.28, 0.7, 12]} /><meshStandardMaterial map={wood} /></mesh>
      {[1.05, 1.45].map((y) => <mesh key={y} position={[3.6, y, ZB + 0.4]} rotation={[Math.PI / 2, 0, 0]}><torusGeometry args={[0.285, 0.018, 5, 16]} /><meshStandardMaterial {...IRON} /></mesh>)}
      <mesh position={[4.35, 1.08, ZB + 0.45]} rotation={[0, 0.3, 0]} castShadow><boxGeometry args={[0.46, 0.36, 0.42]} /><meshStandardMaterial map={wood} /></mesh>
      {[[6.0, "#d6a23a"], [6.25, "#9a3a2a"], [4.8, "#5fd0c0"]].map(([x, c]) => <mesh key={x} position={[x, 1.02, ZB + 0.55]}><cylinderGeometry args={[0.08, 0.08, 0.24, 8]} /><meshStandardMaterial color={c} emissive={c} emissiveIntensity={0.25} transparent opacity={0.85} /></mesh>)}
      {[0, 1].map((k) => <mesh key={k} position={[5.3 + k * 0.1, 0.95, ZB + 0.4 + k * 0.12]} rotation={[0, 0, Math.PI / 2]}><cylinderGeometry args={[0.05, 0.05, 0.5, 8]} /><meshStandardMaterial color="#e2d3b0" /></mesh>)}
    </group>
  );
}

// ═══════════════════════════════════════════════════════════════════
// THE EAST — the painting's right wall: the grimoire on its lectern with
// the violet crystal lamp (ONE point light), the wooden door, the gothic
// window, the bookshelf with the mortars, barrels in the corner.
// ═══════════════════════════════════════════════════════════════════
function EastBlock() {
  const stone = useMemo(() => rep(blockTex(), 2, 1.1), []);
  const wood = useMemo(() => woodTex(), []);
  const plank = useMemo(() => woodTex("ww3d:door", 22, 0xd00), []);
  return (
    <group>
      {/* the block (x 16-19, z 7.4-10.5) and, behind the grimoire, its back (x 17.6-19, z 5-7.4).
          The nook between (x 16-17.6, z 5-7.4) is OPEN to the room so the Codex can be SEEN —
          it stands on a dais there; tiles (16,5)/(16,6) are still solid in the workshop map. */}
      <mesh position={[17.5, EAVE / 2, 8.95]} castShadow receiveShadow><boxGeometry args={[3, EAVE, 3.1]} /><meshStandardMaterial map={stone} roughness={0.9} /></mesh>
      <mesh position={[18.3, EAVE / 2, 6.2]} receiveShadow><boxGeometry args={[1.4, EAVE, 2.4]} /><meshStandardMaterial map={stone} roughness={0.9} /></mesh>
      <mesh position={[17.5, EAVE + 0.05, 7.75]}><boxGeometry args={[3.1, 0.1, 5.6]} /><meshStandardMaterial {...STONE} /></mesh>
      {/* the grimoire's dais */}
      <mesh position={[16.8, 0.08, 6.2]} castShadow receiveShadow><boxGeometry args={[1.6, 0.16, 2.4]} /><meshStandardMaterial {...STONE} /></mesh>
      <mesh position={[16.8, 0.17, 6.2]} rotation={[-Math.PI / 2, 0, 0]}><planeGeometry args={[1.3, 2.1]} /><meshStandardMaterial color="#3a1a4a" roughness={0.9} /></mesh>
      {/* the door on the west face */}
      <group position={[16 - 0.01, 0, 8.35]} rotation={[0, -Math.PI / 2, 0]}>
        <mesh position={[0, 1.05, 0.02]}><boxGeometry args={[1.2, 2.1, 0.06]} /><meshStandardMaterial map={plank} /></mesh>
        {[0.45, 1.65].map((y) => <mesh key={y} position={[0, y, 0.06]}><boxGeometry args={[1.22, 0.07, 0.02]} /><meshStandardMaterial {...IRON} /></mesh>)}
        <mesh position={[0.35, 1.05, 0.08]}><torusGeometry args={[0.08, 0.018, 6, 12]} /><meshStandardMaterial {...IRON} /></mesh>
        {[-1, 1].map((s) => <mesh key={s} position={[s * 0.7, 1.12, 0.05]}><boxGeometry args={[0.18, 2.3, 0.12]} /><meshStandardMaterial {...STONE} /></mesh>)}
        <mesh position={[0, 2.25, 0.05]}><boxGeometry args={[1.58, 0.2, 0.12]} /><meshStandardMaterial {...STONE} /></mesh>
      </group>
      {/* the gothic window, high on the same face — cold light, no lamp */}
      <group position={[16 - 0.02, 0, 9.8]} rotation={[0, -Math.PI / 2, 0]}>
        <mesh position={[0, 2.35, 0.02]}><planeGeometry args={[0.7, 1.0]} /><meshBasicMaterial color="#a9c8f0" toneMapped={false} /></mesh>
        <mesh position={[0, 2.85, 0.02]}><circleGeometry args={[0.35, 14, 0, Math.PI]} /><meshBasicMaterial color="#a9c8f0" toneMapped={false} /></mesh>
        <mesh position={[0, 2.5, 0.04]}><boxGeometry args={[0.04, 1.3, 0.02]} /><meshStandardMaterial {...IRON} /></mesh>
        <mesh position={[0, 2.35, 0.04]}><boxGeometry args={[0.7, 0.04, 0.02]} /><meshStandardMaterial {...IRON} /></mesh>
        <mesh position={[0, 1.83, 0.06]}><boxGeometry args={[0.9, 0.08, 0.12]} /><meshStandardMaterial {...STONE} /></mesh>
      </group>
      {/* the bookshelf against the south face, with the mortars and pestles */}
      <Bookshelf wood={wood} />
      {/* barrels in the corner */}
      {[[18.55, 11.35], [18.1, 11.7]].map(([x, z], i) => (
        <group key={i} position={[x, 0, z]}>
          <mesh position={[0, 0.35, 0]} castShadow><cylinderGeometry args={[0.26, 0.24, 0.7, 12]} /><meshStandardMaterial map={wood} /></mesh>
          {[0.15, 0.55].map((y) => <mesh key={y} position={[0, y, 0]} rotation={[Math.PI / 2, 0, 0]}><torusGeometry args={[0.265, 0.016, 5, 16]} /><meshStandardMaterial {...IRON} /></mesh>)}
        </group>
      ))}
    </group>
  );
}
function Bookshelf({ wood }) {
  const ref = useRef();
  const X0b = 16.2, X1b = 18.8, Z = 10.5, SH = [0.5, 1.05, 1.6];
  const books = useMemo(() => {
    const R = mulberry32(0xb00c);
    const cols = ["#6a2a30", "#2e4a6a", "#4a6a3a", "#6a5a2a", "#3a2a5a", "#7a3a1a"];
    const out = []; let i = 0;
    SH.forEach((y) => { let x = X0b + 0.08; while (x < X1b - 0.12 && i < N_BOOKS) { const w = 0.06 + R() * 0.07, h = 0.28 + R() * 0.16; if (R() < 0.18) { x += 0.3; continue; } out.push({ x: x + w / 2, y: y + 0.03 + h / 2, w, h, c: cols[(R() * cols.length) | 0], lean: R() < 0.15 ? 0.2 : 0 }); x += w + 0.01; i++; } });
    return out;
  }, []);   // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const m = ref.current; if (!m) return;
    const o = new THREE.Object3D(); const col = new THREE.Color();
    books.forEach((b, i) => { o.position.set(b.x, b.y, Z + 0.2); o.rotation.set(0, 0, b.lean); o.scale.set(b.w, b.h, 0.26); o.updateMatrix(); m.setMatrixAt(i, o.matrix); m.setColorAt(i, col.set(b.c)); });
    m.count = books.length;
    m.instanceMatrix.needsUpdate = true; if (m.instanceColor) m.instanceColor.needsUpdate = true;
  }, [books]);
  return (
    <group>
      {[X0b, X1b].map((x) => <mesh key={x} position={[x, 1.05, Z + 0.2]} castShadow><boxGeometry args={[0.08, 2.1, 0.42]} /><meshStandardMaterial map={wood} /></mesh>)}
      {[...SH, 2.12].map((y) => <mesh key={y} position={[(X0b + X1b) / 2, y, Z + 0.2]} castShadow receiveShadow><boxGeometry args={[X1b - X0b, 0.06, 0.42]} /><meshStandardMaterial map={wood} /></mesh>)}
      <instancedMesh ref={ref} args={[null, null, N_BOOKS]}><boxGeometry args={[1, 1, 1]} /><meshStandardMaterial roughness={0.8} /></instancedMesh>
      {/* mortars on the top board */}
      {[16.6, 17.4, 18.3].map((x) => (
        <group key={x} position={[x, 2.15, Z + 0.2]}>
          <mesh position={[0, 0.09, 0]}><cylinderGeometry args={[0.13, 0.09, 0.18, 10, 1, true]} /><meshStandardMaterial color="#9a9aa4" roughness={0.6} side={THREE.DoubleSide} /></mesh>
          <mesh position={[0.05, 0.2, 0]} rotation={[0, 0, -0.5]}><cylinderGeometry args={[0.025, 0.035, 0.28, 6]} /><meshStandardMaterial color="#8a8a94" /></mesh>
        </group>
      ))}
    </group>
  );
}
function Grimoire() {
  const page = useMemo(() => pageTex(), []);
  const wood = useMemo(() => woodTex(), []);
  const crystal = useRef(); const light = useRef(); const book = useRef();
  useFrame(({ clock }) => {
    const t = clock.getElapsedTime();
    const k = 1 + Math.sin(t * 1.7) * 0.12;
    if (crystal.current) { crystal.current.rotation.y = t * 0.4; crystal.current.material.emissiveIntensity = 0.9 * k; }
    if (light.current) light.current.intensity = G(1.5) * k;
    if (book.current) book.current.material.opacity = 0.2 + Math.sin(t * 2.1) * 0.07;
  });
  return (
    <group>
      {/* the lectern, in its nook, the book open toward the room */}
      <group position={[16.7, 0.16, 6.35]} rotation={[0, -0.7, 0]}>
        <mesh position={[0, 0.5, 0]} castShadow><cylinderGeometry args={[0.08, 0.12, 1.0, 6]} /><meshStandardMaterial map={wood} /></mesh>
        <mesh position={[0, 0.04, 0]}><cylinderGeometry args={[0.32, 0.36, 0.08, 8]} /><meshStandardMaterial map={wood} /></mesh>
        <group position={[0, 1.05, 0]} rotation={[-0.45, 0, 0]}>
          <mesh castShadow><boxGeometry args={[0.72, 0.05, 0.5]} /><meshStandardMaterial map={wood} /></mesh>
          <mesh position={[0, 0.04, 0]}><boxGeometry args={[0.66, 0.03, 0.44]} /><meshStandardMaterial color="#3a1a4a" /></mesh>
          <mesh position={[0, 0.062, 0]} rotation={[-Math.PI / 2, 0, 0]}><planeGeometry args={[0.62, 0.4]} /><meshStandardMaterial map={page} emissive="#b388ff" emissiveMap={page} emissiveIntensity={0.55} /></mesh>
          <mesh ref={book} position={[0, 0.2, 0]}><sphereGeometry args={[0.42, 12, 8]} /><meshBasicMaterial color="#b36bff" transparent opacity={0.2} depthWrite={false} toneMapped={false} /></mesh>
        </group>
      </group>
      {/* the violet crystal lamp, as painted: an iron cage on a post */}
      <group position={[17.25, 0.16, 5.5]}>
        <mesh position={[0, 0.6, 0]}><cylinderGeometry args={[0.04, 0.06, 1.2, 6]} /><meshStandardMaterial {...IRON} /></mesh>
        <mesh ref={crystal} position={[0, 1.45, 0]} scale={[1, 1.7, 1]}><octahedronGeometry args={[0.16, 0]} /><meshStandardMaterial color={C.nullus} emissive={C.nullus} emissiveIntensity={0.9} roughness={0.15} toneMapped={false} /></mesh>
        {[0, 1, 2, 3].map((k) => <mesh key={k} position={[Math.sin(k * Math.PI / 2) * 0.17, 1.45, Math.cos(k * Math.PI / 2) * 0.17]}><boxGeometry args={[0.02, 0.6, 0.02]} /><meshStandardMaterial {...IRON} /></mesh>)}
        <mesh position={[0, 1.78, 0]}><coneGeometry args={[0.22, 0.14, 4]} /><meshStandardMaterial {...IRON} /></mesh>
        <pointLight ref={light} position={[0, 1.45, 0.3]} intensity={G(1.5)} distance={6.5} decay={1.5} color="#b388ff" />
      </group>
    </group>
  );
}

// ═══════════════════════════════════════════════════════════════════
// THE FLOOR — the circle (brighter while you stand on it — the 8-second
// secret is WilliesWorkshop's timer, untouched; this only lets the room
// notice you), and the painting's clutter: coal, bones and scrap, the
// violet crystal cluster, a spill of coins and a pot of them.
// ═══════════════════════════════════════════════════════════════════
const CIRCLE_C = [9.5, 9.5];   // WilliesWorkshop: onCircle = |x-9|<=1.5 && |y-9|<=1.5
function SummoningCircle({ tileRef }) {
  const tex = useMemo(() => circleTex(), []);
  const m = useRef(); const halo = useRef(); const heat = useRef(0);
  useFrame(({ clock }, dtRaw) => {
    const t = clock.getElapsedTime(); const dt = Math.min(dtRaw || 0.016, 0.05);
    const p = tileRef.current;
    const on = p && Math.abs(p.x - 9) <= 1.5 && Math.abs(p.y - 9) <= 1.5;
    heat.current += ((on ? 1 : 0) - heat.current) * Math.min(1, dt * (on ? 0.35 : 2));   // slow to wake, quick to sleep
    const h = heat.current;
    if (m.current) { m.current.material.opacity = 0.42 + Math.sin(t * 2.1) * 0.12 + h * 0.5; m.current.rotation.z = t * 0.03 * (1 + h * 4); }
    if (halo.current) { halo.current.material.opacity = h * 0.35; halo.current.scale.setScalar(1 + h * 0.2 + Math.sin(t * 3) * 0.03 * h); }
  });
  return (
    <group position={[CIRCLE_C[0], 0.012, CIRCLE_C[1]]}>
      <mesh ref={m} rotation={[-Math.PI / 2, 0, 0]}><planeGeometry args={[3.3, 3.3]} /><meshBasicMaterial map={tex} transparent opacity={0.45} depthWrite={false} toneMapped={false} blending={THREE.AdditiveBlending} /></mesh>
      <mesh ref={halo} position={[0, 0.6, 0]}><cylinderGeometry args={[1.5, 1.6, 1.2, 24, 1, true]} /><meshBasicMaterial color="#7fe8d8" transparent opacity={0} depthWrite={false} toneMapped={false} blending={THREE.AdditiveBlending} side={THREE.DoubleSide} /></mesh>
    </group>
  );
}
function Clutter() {
  const coinRef = useRef();
  const coins = useMemo(() => { const R = mulberry32(0xc014); return Array.from({ length: N_COINS }, () => ({ x: 15.2 + (R() - 0.5) * 1.3, z: 11.8 + (R() - 0.5) * 0.7, r: R() * 3, tilt: (R() - 0.5) * 0.4 })); }, []);
  const coal = useMemo(() => { const R = mulberry32(0xc0a1); return Array.from({ length: 12 }, () => ({ x: 4.2 + (R() - 0.5) * 0.9, z: 11.3 + (R() - 0.5) * 0.6, s: 0.07 + R() * 0.07, r: R() * 3 })); }, []);
  const bones = useMemo(() => { const R = mulberry32(0xb0e5); return Array.from({ length: 6 }, () => ({ x: 7.3 + (R() - 0.5) * 1.1, z: 8.9 + (R() - 0.5) * 0.6, r: R() * 3, l: 0.2 + R() * 0.2 })); }, []);
  const gems = useMemo(() => [[0, 0.22, 0], [0.12, 0.14, 0.08], [-0.1, 0.12, 0.06], [0.04, 0.1, -0.12], [-0.05, 0.16, -0.05]], []);
  useEffect(() => {
    const m = coinRef.current; if (!m) return;
    const o = new THREE.Object3D();
    coins.forEach((c, i) => { o.position.set(c.x, 0.012, c.z); o.rotation.set(c.tilt, c.r, 0); o.updateMatrix(); m.setMatrixAt(i, o.matrix); });
    m.instanceMatrix.needsUpdate = true;
  }, [coins]);
  return (
    <group>
      <instancedMesh ref={coinRef} args={[null, null, N_COINS]}><cylinderGeometry args={[0.06, 0.06, 0.015, 10]} /><meshStandardMaterial color="#e8b84a" metalness={0.8} roughness={0.3} emissive="#6a4a10" /></instancedMesh>
      {/* the pot of coins */}
      <group position={[6.4, 0, 11.8]}>
        <mesh position={[0, 0.18, 0]} castShadow><cylinderGeometry args={[0.2, 0.17, 0.36, 12]} /><meshStandardMaterial color="#7a7280" metalness={0.5} roughness={0.45} /></mesh>
        <mesh position={[0, 0.36, 0]} rotation={[Math.PI / 2, 0, 0]}><torusGeometry args={[0.2, 0.025, 6, 16]} /><meshStandardMaterial {...IRON} /></mesh>
        <mesh position={[0, 0.37, 0]} scale={[1, 0.35, 1]}><sphereGeometry args={[0.18, 12, 8]} /><meshStandardMaterial color="#e8b84a" metalness={0.8} roughness={0.3} emissive="#6a4a10" /></mesh>
      </group>
      {coal.map((c, i) => <mesh key={i} position={[c.x, c.s * 0.7, c.z]} rotation={[c.r, c.r * 2, 0]} castShadow><dodecahedronGeometry args={[c.s, 0]} /><meshStandardMaterial color="#141218" roughness={0.6} metalness={0.2} /></mesh>)}
      {bones.map((b, i) => <mesh key={i} position={[b.x, 0.03, b.z]} rotation={[Math.PI / 2, 0, b.r]}><cylinderGeometry args={[0.025, 0.025, b.l, 5]} /><meshStandardMaterial color="#d8d0bc" roughness={0.8} /></mesh>)}
      <Skull position={[7.7, 0.14, 9.2]} scale={0.55} rotation={[0, -0.6, 0]} />
      {/* the violet crystal cluster, as painted (and as the outside spills them) */}
      <group position={[13.2, 0, 9.6]}>
        {gems.map((g, k) => <mesh key={k} position={[g[0], g[1] * 0.8, g[2]]} rotation={[0.3 * k, k * 1.3, 0.2 * k]} scale={[1, 1.6, 1]}><octahedronGeometry args={[g[1], 0]} /><meshStandardMaterial color={C.nullus} emissive={C.nullus} emissiveIntensity={0.8} roughness={0.15} toneMapped={false} /></mesh>)}
        <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.01, 0]}><circleGeometry args={[0.6, 14]} /><meshBasicMaterial color={C.nullus} transparent opacity={0.12} depthWrite={false} toneMapped={false} /></mesh>
      </group>
    </group>
  );
}
// the painting's two iron chandeliers, candles lit (emissive — no lamps)
function Chandeliers() {
  const flames = useRef([]);
  useFrame(({ clock }) => { const t = clock.getElapsedTime(); for (let i = 0; i < flames.current.length; i++) { const f = flames.current[i]; if (f) f.scale.set(1, 1.3 + Math.sin(t * 9 + i * 1.7) * 0.25, 1); } });
  const at = [[6.4, 6.6], [14.2, 6.6]];
  let n = 0;
  return at.map(([x, z], ci) => {
    const y = 4.55, top = canopyH(x, z) - 0.05;
    return (
      <group key={ci} position={[x, 0, z]}>
        <mesh position={[0, (y + top) / 2, 0]}><cylinderGeometry args={[0.012, 0.012, top - y, 4]} /><meshStandardMaterial {...IRON} /></mesh>
        <mesh position={[0, y, 0]} rotation={[Math.PI / 2, 0, 0]}><torusGeometry args={[0.55, 0.035, 6, 20]} /><meshStandardMaterial {...IRON} /></mesh>
        {Array.from({ length: 6 }, (_, k) => { const a = k / 6 * Math.PI * 2; const i = n++; return (
          <group key={k} position={[Math.cos(a) * 0.55, y + 0.04, Math.sin(a) * 0.55]}>
            <mesh position={[0, 0.08, 0]}><cylinderGeometry args={[0.03, 0.03, 0.16, 6]} /><meshStandardMaterial color="#e8dcc0" /></mesh>
            <mesh ref={(el) => { flames.current[i] = el; }} position={[0, 0.2, 0]}><sphereGeometry args={[0.03, 6, 6]} /><meshBasicMaterial color="#ffd27a" toneMapped={false} /></mesh>
          </group>); })}
      </group>
    );
  });
}

// ═══════════════════════════════════════════════════════════════════
// THE ◆ MARKERS — the 2D room's diamonds, floating over what each one
// opens. The one the console has selected burns brighter. One useFrame.
// ═══════════════════════════════════════════════════════════════════
function Markers({ items, selRef }) {
  const refs = useRef([]);
  useFrame(({ clock }) => {
    const t = clock.getElapsedTime(); const sel = selRef.current;
    items.forEach((it, i) => {
      const o = refs.current[i]; if (!o) return;
      const on = sel === it.id;
      o.position.y = it.pos[1] + Math.sin(t * 2 + i) * 0.06;
      o.rotation.y = t * (on ? 2.4 : 0.9);
      const s = on ? 1.35 : 1; o.scale.set(s, s * 1.5, s);
      o.material.opacity = on ? 1 : 0.55;
    });
  });
  return items.map((it, i) => (
    <mesh key={it.id} ref={(el) => { refs.current[i] = el; }} position={it.pos}>
      <octahedronGeometry args={[0.09, 0]} />
      <meshBasicMaterial color={it.color} transparent opacity={0.6} depthWrite={false} toneMapped={false} />
    </mesh>
  ));
}

// ═══════════════════════════════════════════════════════════════════
// NAME PLATE · COMPANION — duplicated from Plaza3D (see header ⚠)
// ═══════════════════════════════════════════════════════════════════
const plateTex = (name, title, color) => canvasTex(`plaza3d:plate:${name}|${title || ""}|${color}`, 256, 96, (g, w, h) => {
  g.clearRect(0, 0, w, h);
  const r = 18, x = 8, y = title ? 6 : 22, bw = w - 16, bh = title ? 84 : 52;
  g.fillStyle = "rgba(4,2,14,0.86)"; g.beginPath(); g.roundRect(x, y, bw, bh, r); g.fill();
  g.strokeStyle = color + "88"; g.lineWidth = 2; g.stroke();
  g.textAlign = "center"; g.textBaseline = "middle"; g.shadowColor = "#000"; g.shadowBlur = 4;
  g.fillStyle = color; g.font = "bold 30px Georgia, serif"; g.fillText(name, w / 2, y + 26);
  if (title) { g.fillStyle = "#d8cfe8"; g.font = "italic 20px Georgia, serif"; g.fillText(title, w / 2, y + 60); }
});
const _wp = new THREE.Vector3();
function NamePlate({ name, title, color = "#f0cc60", y }) {
  const ref = useRef();
  const tex = useMemo(() => plateTex(name, title, color), [name, title, color]);
  useFrame(({ camera }) => { const o = ref.current; if (!o) return; o.getWorldPosition(_wp); o.rotation.y = Math.atan2(camera.position.x - _wp.x, camera.position.z - _wp.z); });
  return <mesh ref={ref} position={[0, y, 0]}><planeGeometry args={[1.5, 0.56]} /><meshBasicMaterial map={tex} transparent depthWrite={false} toneMapped={false} /></mesh>;
}
const RIG_SLOTS = ["head", "armL", "armR", "legL", "legR", "tail"];
function KitBody({ info, ctl }) {
  const outer = useRef(); const inner = useRef(); const fitted = useRef(false); const rig = useRef(null);
  const yaw = useRef(0); const ph = useRef(0);
  const pal = useMemo(() => getBeingPalette(info.lineage), [info.lineage]);
  useFrame(({ clock }, dtRaw) => {
    const o = outer.current, g = inner.current; if (!o || !g) return;
    const dt = Math.min(dtRaw || 0.016, 0.05); const t = clock.getElapsedTime();
    if (!fitted.current) {
      const mm = measureSolid(g);
      if (mm && isFinite(mm.h) && mm.h > 1e-3) {
        const s = COMPANION_H / mm.h; g.scale.setScalar(s); g.position.set(-mm.cx * s, -mm.minY * s, -mm.cz * s);
        const R = {}; RIG_SLOTS.forEach((k) => { const n = g.getObjectByName("rig_" + k); if (n) R[k] = { o: n, bx: n.rotation.x, bz: n.rotation.z }; });
        rig.current = R; fitted.current = true;
      }
    }
    const f = ctl.facing || { x: 0, y: 1 };
    const want = Math.atan2(f.x || 0, f.y === undefined ? 1 : f.y);
    let d = want - yaw.current; while (d > Math.PI) d -= Math.PI * 2; while (d < -Math.PI) d += Math.PI * 2;
    yaw.current += d * Math.min(1, dt * 9);
    const speed = ctl.speed || 0; ph.current += speed * dt * 9;
    o.rotation.set(0, yaw.current, 0);
    o.position.y = speed ? Math.abs(Math.sin(ph.current)) * 0.05 : 0;
    const R = rig.current; if (!R) return;
    const legs = speed ? Math.sin(ph.current) * 0.5 : 0, arms = speed ? -Math.sin(ph.current) * 0.3 : 0;
    const set = (k, x, z) => { const r = R[k]; if (r) { r.o.rotation.x = r.bx + x; r.o.rotation.z = r.bz + z; } };
    set("legL", legs, 0); set("legR", -legs, 0); set("armL", arms, 0); set("armR", -arms, 0);
    if (R.head) R.head.o.rotation.x = R.head.bx + Math.sin(t * 0.7) * 0.06;
    if (R.tail) R.tail.o.rotation.z = R.tail.bz + Math.sin(t * 3.1) * 0.25;
  });
  return (
    <group ref={outer}>
      <mesh position={[0, 0.005, 0]} rotation={[-Math.PI / 2, 0, 0]}><circleGeometry args={[0.36, 16]} /><meshBasicMaterial color={pal?.glow || "#c8963c"} transparent opacity={0.14} depthWrite={false} toneMapped={false} /></mesh>
      <group ref={inner}><IEBeing being={info.lineage} tier={info.tier} /></group>
    </group>
  );
}
function Companion({ visRef, facingRef, companionId, radiantTier }) {
  const grace = renderableGrace(radiantTier);
  const g = useRef(); const ctl = useRef({ facing: { x: 0, y: 1 }, speed: 0 }).current;
  const info = useMemo(() => companionBodyOf(companionId), [companionId]);
  const last = useRef(null);
  useFrame((_, dtRaw) => {
    const o = g.current; if (!o) return;
    const p = visRef.current, f = facingRef.current || { x: 0, y: 1 };
    const dt = Math.min(dtRaw || 0.016, 0.05);
    const k = 1 - Math.pow(LERP_K * 8, dt);
    const tx = p.x - (f.x || 0) + 0.5, tz = p.y - (f.y ?? 1) + 0.5;   // a tile behind you
    if (!last.current) { o.position.set(tx, 0, tz); last.current = { x: tx, z: tz }; }
    o.position.x += (tx - o.position.x) * k; o.position.z += (tz - o.position.z) * k;
    const mv = Math.hypot(o.position.x - last.current.x, o.position.z - last.current.z);
    ctl.speed = mv > 0.0025 ? 1 : 0;
    ctl.facing = mv > 0.0025 ? { x: o.position.x - last.current.x, y: o.position.z - last.current.z } : { x: f.x || 0, y: f.y ?? 1 };
    last.current = { x: o.position.x, z: o.position.z };
  });
  const glow = grace ? GRACES[grace].color : null;
  return (
    <group ref={g}>
      {info ? <KitBody info={info} ctl={ctl} /> : (
        <Html center position={[0, 0.55, 0]} style={{ pointerEvents: "none" }} zIndexRange={[5, 0]}>
          <img src={getCompanionSprite(companionId)} alt="" style={{ width: 54, height: 54, objectFit: "contain", imageRendering: "pixelated", filter: glow ? `drop-shadow(0 0 6px ${glow})` : "drop-shadow(0 4px 6px rgba(0,0,0,.7))" }} />
        </Html>
      )}
      {grace && <GraceAura3D graceId={grace} height={info ? COMPANION_H : 1.0} />}
    </group>
  );
}

// ═══════════════════════════════════════════════════════════════════
// YOU — the workshop steps a whole tile every 130ms; the body glides.
// `visRef` is the smoothed position; the camera and the companion read it.
// ═══════════════════════════════════════════════════════════════════
function Me({ tileRef, facingRef, visRef, gender, name, title }) {
  const g = useRef(); const ctl = useRef({ facing: { x: 0, y: -1 }, speed: 0 }).current;
  const id = folkIdFor(gender); const still = useRef(0);
  useFrame((_, dtRaw) => {
    const o = g.current; if (!o) return;
    const dt = Math.min(dtRaw || 0.016, 0.05);
    const tp = tileRef.current, v = visRef.current;
    const dx = tp.x - v.x, dy = tp.y - v.y;
    if (Math.abs(dx) > 3 || Math.abs(dy) > 3) { v.x = tp.x; v.y = tp.y; }   // a teleport snaps
    else { const k = 1 - Math.exp(-14 * dt); v.x += dx * k; v.y += dy * k; }
    o.position.set(v.x + 0.5, 0, v.y + 0.5);
    if (Math.abs(dx) + Math.abs(dy) > 0.01) still.current = 0.12; else still.current -= dt;
    ctl.speed = still.current > 0 ? 1 : 0;
    const f = facingRef.current || { x: 0, y: -1 };
    ctl.facing = { x: f.x || 0, y: f.y ?? 1 };
  });
  return (
    <group ref={g}>
      <FolkBody id={id} ctl={ctl} />
      <NamePlate name={name || "—"} title={title} y={folkHeight(id) + 0.42} />
    </group>
  );
}
function Willy() {
  const ctl = useRef({ facing: { x: 0, y: 1 }, speed: 0 }).current;
  return (
    <group position={BENCH_SHIFT}>
      <SculptedBench rect={HERALD_RECT} />
      <group position={[PLAZA_WILLY[0], 0, PLAZA_WILLY[1]]}><FolkBody id="willy_forge" ctl={ctl} /></group>
    </group>
  );
}

// ═══════════════════════════════════════════════════════════════════
// THE CAMERA RIG — the Plaza's contract, this room's framing
// ═══════════════════════════════════════════════════════════════════
const TMP_A = new THREE.Vector3(); const TMP_B = new THREE.Vector3();
function CameraRig({ visRef }) {
  const { camera, gl, size } = useThree();
  const controls = useRef();
  const lastTarget = useRef(new THREE.Vector3(visRef.current.x + 0.5, 1, visRef.current.y + 0.5));
  const lastReset = useRef(-1);   // the first frame is a DEFAULT press — no lerp in from nowhere
  const fit = size.width < size.height ? Math.min(1.5, 0.8 * (size.height / size.width)) : 1;
  const BASE_DIST = 6.4 * fit, BASE_HEIGHT = 4.3 * fit;
  useEffect(() => () => resetGroundForward(), []);
  useFrame(() => {
    const c = controls.current; if (!c) return;
    if (typeof window !== "undefined" && window.__plazaCamFree) { if (window.__plazaLook) c.target.fromArray(window.__plazaLook); return; }
    const p = visRef.current;
    const tx = p.x + 0.5, ty = 1.0, tz = p.y + 0.5;
    camera.getWorldDirection(TMP_A); TMP_A.y = 0;
    if (TMP_A.lengthSq() > 1e-6) { TMP_A.normalize(); setGroundForward(TMP_A.x, TMP_A.z); }
    const tok = resetToken();
    if (tok !== lastReset.current) {
      lastReset.current = tok; const m = viewMult();
      camera.position.set(tx, ty + BASE_HEIGHT * m, tz + BASE_DIST * m);
      c.target.set(tx, ty, tz); lastTarget.current.set(tx, ty, tz); c.update(); return;
    }
    if (isPosed()) {
      TMP_B.set(tx - lastTarget.current.x, ty - lastTarget.current.y, tz - lastTarget.current.z);
      camera.position.add(TMP_B); c.target.set(tx, ty, tz); lastTarget.current.set(tx, ty, tz); c.update(); return;
    }
    const m = viewMult();
    TMP_B.set(tx, ty + BASE_HEIGHT * m, tz + BASE_DIST * m);
    camera.position.lerp(TMP_B, 0.055);
    c.target.lerp(TMP_A.set(tx, ty, tz), 0.12);
    lastTarget.current.copy(c.target); c.update();
  });
  return (
    <OrbitControls ref={controls} makeDefault enablePan={false}
      onStart={() => setPosed(true)}
      minDistance={2.0} maxDistance={18}
      maxPolarAngle={Math.PI / 1.9}
      enableDamping dampingFactor={0.08} domElement={gl.domElement} />
  );
}

// ═══════════════════════════════════════════════════════════════════
// THE SCENE
// ═══════════════════════════════════════════════════════════════════
function Scene({ tileRef, facingRef, gender, playerName, title, companionId, radiantTier, markers, selRef }) {
  const visRef = useRef({ x: tileRef.current.x, y: tileRef.current.y });
  // the moonlight aims at the ROOM, not the world origin, or its shadow box misses it
  const moonAim = useMemo(() => { const o = new THREE.Object3D(); o.position.set(CX, 0, 9); return o; }, []);
  return (
    <>
      <ambientLight intensity={G(0.78)} color="#a898d0" />
      <hemisphereLight args={["#6a5a90", "#1a0f1a", G(0.8)]} />
      {/* moonlight in through the mouth — the only shadow-caster */}
      <primitive object={moonAim} />
      <directionalLight target={moonAim} position={[CX - 4, 11, 22]} intensity={G(1.1)} color="#c8c0ff" castShadow
        shadow-mapSize-width={1024} shadow-mapSize-height={1024}
        shadow-camera-left={-12} shadow-camera-right={12} shadow-camera-top={9} shadow-camera-bottom={-9}
        shadow-camera-near={1} shadow-camera-far={45} shadow-bias={-0.0006} shadow-normalBias={0.02} />
      <fog attach="fog" args={["#0a0612", 18, 48]} />

      <Shell />
      <Hearth />
      <DungeonDoor />
      <PotionShelves />
      <Worktop />
      <Cauldron />
      <Chest />
      <EastBlock />
      <Grimoire />
      <Chandeliers />
      <SummoningCircle tileRef={tileRef} />
      <Clutter />
      <Willy />
      <Markers items={markers} selRef={selRef} />

      <Me tileRef={tileRef} facingRef={facingRef} visRef={visRef} gender={gender} name={playerName} title={title} />
      {companionId && <Companion visRef={visRef} facingRef={facingRef} companionId={companionId} radiantTier={radiantTier} />}

      <CameraRig visRef={visRef} />
      <FrameWatch />
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════
// THE HOST — React.memo; the workshop re-renders on every step, and
// every live value reaches the scene through a ref.
// ═══════════════════════════════════════════════════════════════════
function Workshop3DImpl({ tileRef, facingRef, selRef, interactables = [], gender, playerName, title, companionId, radiantTier, mobile = false }) {
  const [, force] = useState(0);
  useEffect(() => subscribe(() => force((v) => v + 1)), []);
  const markers = useMemo(() => interactables.filter((o) => !o.secret).map((o) => ({
    id: o.id, color: o.color && o.color.length === 7 ? o.color : "#c9a84c",
    pos: ANCHOR[o.id] || [o.x + 0.5, 1.6, o.y + 0.5],
  })), [interactables]);
  const view = getView(); const posed = isPosed();
  const sp = tileRef.current;
  return (
    <div style={{ position: "absolute", inset: 0, background: "#07040f" }}>
      <Canvas
        dpr={[1, 1.5]} shadows
        camera={{ fov: 52, near: 0.15, far: 200, position: [sp.x + 0.5, 5.3, sp.y + 7] }}
        gl={{ antialias: true, powerPreference: "high-performance", toneMapping: THREE.ACESFilmicToneMapping, toneMappingExposure: G(LIGHT.exposure) }}
        onCreated={(state) => {
          state.gl.setClearColor("#07040f");
          if (process.env.NODE_ENV === "development") { window.__forgeGL = state.gl; window.__forgeScene = state.scene; window.__forgeCam = state.camera; }
        }}
      >
        <Scene tileRef={tileRef} facingRef={facingRef} gender={gender} playerName={playerName} title={title}
          companionId={companionId} radiantTier={radiantTier} markers={markers} selRef={selRef} />
      </Canvas>
      <CameraBar view={view} posed={posed} force={force} mobile={mobile} />
    </div>
  );
}
export default React.memo(Workshop3DImpl);
